/**
 * Date-aware task status.
 *
 * The backend `status` only says what the task was last told to do ("scheduled"). It
 * never flips a task to "missed" when its time passes without anything running, so a
 * task dated last September can still read SCHEDULED. This derives what the user
 * actually needs to see: did it run, did it fail, was it never executed, is it due.
 */

export type EffectiveTaskKey =
  | 'executed'
  | 'failed'
  | 'cancelled'
  | 'rejected'
  | 'paused'
  | 'running'
  | 'awaiting_approval'
  | 'approval_overdue'
  | 'not_executed'
  | 'due_now'
  | 'due_today'
  | 'upcoming'
  | 'unknown';

export type EffectiveTaskTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'live';

export interface EffectiveTaskStatus {
  key: EffectiveTaskKey;
  label: string;
  tone: EffectiveTaskTone;
  /** One-line explanation shown under the badge, e.g. "Missed 3 days ago". */
  hint: string;
  /** True when the task's time has passed and it is still not finished. */
  isPastDue: boolean;
  /** True once nothing more will happen to the task. */
  isFinal: boolean;
}

export interface TaskStatusInput {
  status?: string;
  scheduled_at?: string;
  executed?: boolean;
  executed_at?: string;
  approval_status?: string;
  completion_reason?: string;
}

/** Tasks within this window of their time are "due now", not missed. */
export const DUE_NOW_GRACE_MS = 15 * 60 * 1000;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function parseTaskDate(value: any): Date | null {
  if (value == null || value === '') {
    return null;
  }
  const d = typeof value === 'number'
    ? new Date(value < 1e12 ? value * 1000 : value)
    : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** "29 Sep 2026, 3:45 PM" — always includes the year so old tasks can't be misread. */
export function formatTaskDateTime(value: any): string {
  const d = parseTaskDate(value);
  if (!d) {
    return '—';
  }
  const h = d.getHours();
  const mins = `${d.getMinutes()}`.padStart(2, '0');
  const h12 = h % 12 || 12;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${h12}:${mins} ${h >= 12 ? 'PM' : 'AM'}`;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "3 days ago" / "in 2 hours" / "yesterday". */
export function relativeTaskTime(value: any, now: Date = new Date()): string {
  const d = parseTaskDate(value);
  if (!d) {
    return '';
  }
  const diff = d.getTime() - now.getTime();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const hours = Math.round(abs / 3600000);
  const dayGap = Math.round((startOfDay(d) - startOfDay(now)) / 86400000);
  const past = diff < 0;
  const wrap = (s: string) => (past ? `${s} ago` : `in ${s}`);

  if (mins < 1) {
    return 'just now';
  }
  if (mins < 60) {
    return wrap(`${mins} min`);
  }
  if (Math.abs(dayGap) === 0) {
    return wrap(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (dayGap === -1) {
    return 'yesterday';
  }
  if (dayGap === 1) {
    return 'tomorrow';
  }
  const days = Math.abs(dayGap);
  if (days < 30) {
    return wrap(`${days} days`);
  }
  if (days < 365) {
    const m = Math.round(days / 30);
    return wrap(`${m} ${m === 1 ? 'month' : 'months'}`);
  }
  const y = Math.round(days / 365);
  return wrap(`${y} ${y === 1 ? 'year' : 'years'}`);
}

function result(
  key: EffectiveTaskKey, label: string, tone: EffectiveTaskTone, hint: string,
  isPastDue: boolean, isFinal: boolean,
): EffectiveTaskStatus {
  return { key, label, tone, hint, isPastDue, isFinal };
}

export function getEffectiveTaskStatus(task: TaskStatusInput, now: Date = new Date()): EffectiveTaskStatus {
  const status = ((task && task.status) || '').toLowerCase();
  const approval = ((task && task.approval_status) || '').toLowerCase();
  const scheduled = parseTaskDate(task && task.scheduled_at);
  const executedAt = parseTaskDate(task && task.executed_at);
  const reason = ((task && task.completion_reason) || '').trim();

  // 1. Terminal states first: these never depend on the clock.
  if ((task && task.executed === true) || status === 'completed') {
    return result('executed', 'Executed', 'success',
      executedAt ? `Ran ${relativeTaskTime(executedAt, now)}` : (reason || 'Completed'), false, true);
  }
  if (status === 'failed') {
    return result('failed', 'Failed', 'danger', reason || 'The attempt did not succeed', false, true);
  }
  if (status === 'cancelled') {
    if (approval === 'rejected' || reason.toLowerCase() === 'rejected') {
      return result('rejected', 'Rejected', 'neutral', 'Not approved, so it never ran', false, true);
    }
    return result('cancelled', 'Cancelled', 'neutral', reason || 'Cancelled before it ran', false, true);
  }

  const diff = scheduled ? scheduled.getTime() - now.getTime() : null;
  const pastDue = diff !== null && diff < -DUE_NOW_GRACE_MS;

  if (status === 'running') {
    const stale = diff !== null && diff < -24 * 3600 * 1000;
    return result('running', 'Running', 'live',
      stale ? `Still marked running since ${relativeTaskTime(scheduled, now)}` : 'In progress now', stale, false);
  }
  if (status === 'paused') {
    return result('paused', 'Paused', 'warning',
      pastDue ? `Paused, was due ${relativeTaskTime(scheduled, now)}` : 'Paused, will not run until resumed',
      pastDue, false);
  }

  // 2. Open states: the clock decides whether this is still ahead of us or was missed.
  if (status === 'awaiting_approval' || approval === 'pending') {
    return pastDue
      ? result('approval_overdue', 'Not approved in time', 'danger',
          `Needed approval ${relativeTaskTime(scheduled, now)}`, true, false)
      : result('awaiting_approval', 'Awaiting approval', 'warning',
          scheduled ? `Runs ${relativeTaskTime(scheduled, now)} once approved` : 'Needs your approval', false, false);
  }
  if (!scheduled) {
    return result('unknown', status ? status.replace(/_/g, ' ') : 'No date', 'neutral', 'No schedule time', false, false);
  }
  if (pastDue) {
    return result('not_executed', 'Not executed', 'danger', `Missed ${relativeTaskTime(scheduled, now)}`, true, false);
  }
  if (diff <= DUE_NOW_GRACE_MS && diff >= -DUE_NOW_GRACE_MS) {
    return result('due_now', 'Due now', 'live', 'Due within minutes', false, false);
  }
  if (startOfDay(scheduled) === startOfDay(now)) {
    return result('due_today', 'Due today', 'info', `Runs ${relativeTaskTime(scheduled, now)}`, false, false);
  }
  return result('upcoming', 'Scheduled', 'info', `Runs ${relativeTaskTime(scheduled, now)}`, false, false);
}

/** Buckets for the status cards. Every task lands in exactly one, so the cards add up. */
export type TaskBucket = 'executed' | 'upcoming' | 'not_executed' | 'failed' | 'awaiting_approval' | 'paused' | 'running' | 'cancelled';

export function bucketForEffective(s: EffectiveTaskStatus): TaskBucket {
  switch (s.key) {
    case 'executed': return 'executed';
    case 'failed': return 'failed';
    case 'cancelled':
    case 'rejected': return 'cancelled';
    case 'paused': return 'paused';
    case 'running': return 'running';
    case 'awaiting_approval': return 'awaiting_approval';
    case 'approval_overdue':
    case 'not_executed': return 'not_executed';
    default: return 'upcoming';
  }
}
